import { BaseViewModel } from "@iot-platform/core";
import {
    SpatialLevel,
    SpatialLevels,
} from "@station/domain/models/spatial-level.model.ts";
import type { Station } from "@station/domain/models/station.model.ts";
import { SpatialLevelGetAllUseCase } from "@station/domain/usecases/spatial-level-get-all.usecase";
import { StationAddSelectedUseCase } from "@station/domain/usecases/station-add-selected.usecase.ts";
import { StationGetSelectedUseCase } from "@station/domain/usecases/station-get-selected.usecase";
import { inject, injectable } from "inversify";
import { action, makeObservable, observable, runInAction } from "mobx";

@injectable()
export class StationMapMobileViewmodel extends BaseViewModel {
    @observable
    spatialLevels: SpatialLevels = SpatialLevels.empty();

    @observable
    markers: Readonly<Station>[] = [];

    @observable
    selectedStations: readonly Readonly<Station>[] = [];

    constructor(
        @inject(SpatialLevelGetAllUseCase)
        private readonly spatialLevelGetAllUseCase: SpatialLevelGetAllUseCase,
        @inject(StationAddSelectedUseCase)
        private readonly stationAddSelectedUseCase: StationAddSelectedUseCase,
        @inject(StationGetSelectedUseCase)
        private readonly stationGetSelectedUseCase: StationGetSelectedUseCase,
    ) {
        super();
        makeObservable(this);
    }

    @action
    override async didMount(): Promise<void> {
        await this.initViewData();

        this.addSub(
            this.stationGetSelectedUseCase.execute().subscribe((stations) => {
                runInAction(() => {
                    this.selectedStations = stations;
                    this.initMarkers(
                        this.selectedStations.length > 0
                            ? this.selectedStations
                            : undefined,
                    );
                });
            }),
        );
    }

    async initViewData(): Promise<void> {
        await this.fetchSpatialLevels();
        this.initMarkers();
    }

    private async fetchSpatialLevels(): Promise<void> {
        this.spatialLevels = await this.spatialLevelGetAllUseCase.execute();
    }

    @action
    initMarkers(stations?: readonly Readonly<Station>[]): void {
        runInAction(() => {
            const validMarkers: Readonly<Station>[] = [];

            if (stations) {
                stations.forEach((station) => {
                    if (station.latitude && station.longitude) {
                        validMarkers.push(station);
                    }
                });
            } else {
                const traverseLevels = (
                    level: Readonly<SpatialLevel>,
                ): void => {
                    level.stations.forEach((station) => {
                        if (station.latitude && station.longitude) {
                            validMarkers.push(station);
                        }
                    });

                    level.children?.forEach((child) => {
                        traverseLevels(child);
                    });
                };

                this.spatialLevels.spatialLevels.forEach((level) => {
                    traverseLevels(level);
                });
            }

            this.markers = validMarkers;
        });
    }

    @action
    addStation(station: Station): void {
        this.stationAddSelectedUseCase.execute(station);
    }
}
